import type { ReactNode } from 'react';
import { Size } from '@bms2/shared';
import { Checkbox } from '../controls';
import type { RowDataAttributes } from './DetailList';
import styles from './DataTable.module.css';

/**
 * 🔒 The data table (piece 4; T3224): the one function a page's fixed table goes
 * through, the tables the bare-table rule lets stand because nobody re-scopes,
 * pages or counts them. Sixteen page files each drew a raw table with their own
 * copy of the header rule before; `rawTables.test.ts` holds the list of those
 * still raw, and it only shrinks.
 *
 * 🔨 It is not the List Viewer. A list somebody sorts, pages, filters or counts
 * goes through the List Viewer, a line-item grid through the Item Table, and a
 * readout of a card's rows through the detail list.
 *
 * Rows come in one of two forms. The children form takes the page's own rows,
 * or its own row groups with `ownBodies`, so a page keeps its folded sections
 * and a panel opened under a row. The rows form takes a row list and one cell
 * function, and is the one that draws a pick column and a row-header cell.
 */
export type DataTableAlign = 'start' | 'end';

export interface DataTableColumn {
  /** The column's key, which the cell function is asked for and the header cell carries as `data-column`. */
  key: string;
  /** The header's words, already through `t()`. */
  header: ReactNode;
  /** Where the header and the cells sit; at the start where none is named. */
  align?: DataTableAlign;
  /** The header is read by a screen reader and not drawn, for a column of arrows or acts. */
  headerHidden?: boolean;
  /** The column takes only its content's width. */
  narrow?: boolean;
  /** The cells are figures: tabular numerals, never wrapped. */
  numeric?: boolean;
  /** The header cell's own `data-` hooks (T3272), for a column a suite or a stylesheet finds by what it holds. */
  headData?: RowDataAttributes;
  /** A page's own class on the header cell, for the one column that reads differently from the rest (T3272). */
  headClass?: string | undefined;
}

/** The pick column: a checkbox before each row, and a box in its head that picks the page where the page passes that act. */
export interface DataTablePick<Row> {
  /** Each row's box label, read by a screen reader and not drawn; the head's hidden label where it carries no box. */
  label: string;
  isPicked: (row: Row) => boolean;
  onPick: (row: Row, on: boolean) => void;
  /** Each row's box name, which a suite reads. */
  name: (row: Row) => string;
  /** Where passed, the head carries a box that picks or clears every row of the page. */
  onPickPage?: (on: boolean) => void;
  /** The head box's label; the row label where none is named. */
  pageLabel?: string;
}

interface DataTableCommon extends RowDataAttributes {
  columns: readonly DataTableColumn[];
  /** The head stays in place while the rows scroll inside the page's own pane. */
  stickyHead?: boolean;
  /** No cell wraps, for a fixed reference list read across one line. */
  nowrap?: boolean;
  /** The table's accessible name, where no heading beside it gives one. */
  label?: string;
}

export interface DataTableChildrenProps extends DataTableCommon {
  /** The page's own rows; with `ownBodies`, its own row groups, each a `tbody`. */
  children: ReactNode;
  ownBodies?: boolean;
}

export interface DataTableRowsProps<Row> extends DataTableCommon {
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  /** One cell's content, asked for once per row and column. */
  cell: (row: Row, column: string) => ReactNode;
  /** The row's own `data-` hooks. */
  rowData?: (row: Row) => RowDataAttributes;
  /** One body cell's own `data-` hooks, asked for once per row and column (T3272). */
  cellData?: (row: Row, column: string) => RowDataAttributes | undefined;
  /** A page's own class on one body cell, for a cell that reads as chosen (T3272). */
  cellClass?: (row: Row, column: string) => string | undefined;
  /** What the body says in one full-width row when there are no rows; nothing is drawn where it is absent (T3272). */
  empty?: ReactNode;
  /** The first column's cell is drawn as the row's header. */
  rowHeader?: boolean;
  pick?: DataTablePick<Row>;
}

export type DataTableProps<Row> = DataTableChildrenProps | DataTableRowsProps<Row>;

export const dataTableDocumentedStates = ['plain', 'pick', 'rowHeader', 'sticky'] as const;

function cellClass(column: DataTableColumn, own?: string): string | undefined {
  const classes = [
    column.align === 'end' ? styles.end : '',
    column.narrow ? styles.narrow : '',
    column.numeric ? styles.numeric : '',
    own ?? '',
  ].filter(Boolean);
  return classes.length === 0 ? undefined : classes.join(' ');
}

function isRows<Row>(props: DataTableProps<Row>): props is DataTableRowsProps<Row> {
  return 'rows' in props;
}

export function DataTable<Row>(props: DataTableProps<Row>) {
  const { columns, stickyHead = false, nowrap = false, label } = props;
  const data = Object.fromEntries(Object.entries(props).filter(([name]) => name.startsWith('data-')));
  const pick = isRows(props) ? props.pick : undefined;
  const rows = isRows(props) ? props.rows : [];
  const pagePicked = rows.length > 0 && pick !== undefined && rows.every((row) => pick.isPicked(row));
  const pageSome = pick !== undefined && !pagePicked && rows.some((row) => pick.isPicked(row));
  const tableClass = [styles.table, stickyHead ? styles.sticky : '', nowrap ? styles.nowrap : ''].filter(Boolean).join(' ');
  return (
    <table className={tableClass} data-data-table={isRows(props) ? 'rows' : 'children'} {...(label === undefined ? {} : { 'aria-label': label })} {...data}>
      <thead>
        <tr>
          {pick ? (
            <th scope="col" className={styles.narrow} data-column="pick">
              {pick.onPickPage ? (
                <Checkbox label={pick.pageLabel ?? pick.label} labelHidden checked={pagePicked} indeterminate={pageSome} onChange={pick.onPickPage} size={Size.Small} />
              ) : <span className={styles.hidden}>{pick.label}</span>}
            </th>
          ) : null}
          {columns.map((column) => (
            <th key={column.key} scope="col" className={cellClass(column, column.headClass)} data-column={column.key} {...(column.headData ?? {})}>
              {column.headerHidden ? <span className={styles.hidden}>{column.header}</span> : column.header}
            </th>
          ))}
        </tr>
      </thead>
      {isRows(props) ? (
        <tbody>
          {props.rows.map((row) => (
            <tr key={props.rowKey(row)} {...(props.rowData?.(row) ?? {})}>
              {pick ? (
                <td className={styles.narrow}>
                  <Checkbox label={pick.label} labelHidden checked={pick.isPicked(row)} onChange={(on) => pick.onPick(row, on)} name={pick.name(row)} size={Size.Small} />
                </td>
              ) : null}
              {columns.map((column, index) => (props.rowHeader && index === 0
                ? <th key={column.key} scope="row" className={cellClass(column, props.cellClass?.(row, column.key))} {...(props.cellData?.(row, column.key) ?? {})}>{props.cell(row, column.key)}</th>
                : <td key={column.key} className={cellClass(column, props.cellClass?.(row, column.key))} {...(props.cellData?.(row, column.key) ?? {})}>{props.cell(row, column.key)}</td>))}
            </tr>
          ))}
          {props.rows.length === 0 && props.empty !== undefined && props.empty !== null ? (
            <tr><td colSpan={columns.length + (pick ? 1 : 0)}>{props.empty}</td></tr>
          ) : null}
        </tbody>
      ) : props.ownBodies ? props.children : <tbody>{props.children}</tbody>}
    </table>
  );
}
